import { asRequiredField, nullableWithDefault } from "src/services/zodExtra";
import { z } from "zod";

export interface BaseAddress {
  street_address: string;
  street_address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface Location {
  type: "Point";
  coordinates: [number, number];
}

/* 
  Address Validator for any address format that also uses this schema
*/

export const AddressValidationSchema = z.object({
  street_address: z.string().min(1, "Street address is required"),
  street_address_line_2: z.string(),
  city: z.string().min(1, "City is required"),
  state: z.string().length(2, "A 2 character state is required"),
  zip_code: z.string().length(5, "A 5 digit zip code is required"),
});

export type AddressValidation = z.infer<typeof AddressValidationSchema>;

/*

  Person
*/

// data model
export const PersonAddressSchema = z.object({
  street_address: z.string(),
  street_address_line_2: z.string(),
  city: z.string(),
  state: z.string(),
  zip_code: z.string(),
});

export type PersonAddress = z.infer<typeof PersonAddressSchema>;

// form
export const PersonAddressFormSchema = z.object({
  street_address: nullableWithDefault(z.string(), ""),
  street_address_line_2: nullableWithDefault(z.string(), ""),
  city: nullableWithDefault(z.string(), ""),
  state: nullableWithDefault(z.string(), ""),
  zip_code: nullableWithDefault(z.string(), ""),
});

export type PersonAddressForm = z.infer<typeof PersonAddressFormSchema>;

export const PersonAddressValidator = asRequiredField(
  PersonAddressFormSchema,
  "street_address",
  "city",
  "state",
  "zip_code"
);

/*
  School
*/

// At present, the schemas for school and person profile are identical.
// Treating them as their own types will makie it easier to maintain the code if these schemas diverege later.

// data model
export const SchoolAddressSchema = PersonAddressFormSchema;

export type SchoolAddress = z.infer<typeof PersonAddressFormSchema>;

// form
export const SchoolAddressFormSchema = PersonAddressFormSchema;

export type SchoolAddressForm = z.infer<typeof SchoolAddressFormSchema>;

export const SchoolAddressValidator = asRequiredField(
  SchoolAddressFormSchema,
  "street_address",
  "city",
  "state",
  "zip_code"
);

/*
  Addresses used by form address answers
*/

// data model
export const FormAddressSchema = PersonAddressSchema.merge(
  z.object({
    geom: z.nullable(
      z.object({
        type: z.literal("Point").default("Point"),
        coordinates: z.tuple([z.number(), z.number()]),
      })
    ),
  })
);

// address book
// these are specializations of the person address datatype.
export const CURRENT_ADDRESS = "currentAddress";
const CurrentAddressSchema = FormAddressSchema.extend({
  kind: z.literal(CURRENT_ADDRESS),
  user_id: z.string().nullish(),
});
export type CurrentAddress = z.infer<typeof CurrentAddressSchema>;

export const EXISTING_ADDRESS = "existingAddress";
const ExistingAddressSchema = FormAddressSchema.extend({
  kind: z.literal(EXISTING_ADDRESS),
  user_id: z.string().nullish(),
  user_address_id: z.string().uuid(),
});
export type ExistingAddress = z.infer<typeof ExistingAddressSchema>;

export const NO_ADDRESS = "noAddress";
const EmptyAddressSchema = FormAddressSchema.extend({
  kind: z.literal(NO_ADDRESS),
  user_id: z.string().nullish(),
});
export type EmptyAddress = z.infer<typeof ExistingAddressSchema>;

export type FormAddress = z.infer<typeof FormAddressSchema>;

export const AddressBookAnswerSchema = z.discriminatedUnion("kind", [
  CurrentAddressSchema,
  ExistingAddressSchema,
  EmptyAddressSchema,
]);
export type AddressBookAnswer = z.infer<typeof AddressBookAnswerSchema>;

export const AddressFormAnswerSchema = z.union([
  FormAddressSchema,
  AddressBookAnswerSchema,
]);
export type AddressFormAnswer = z.infer<typeof AddressFormAnswerSchema>;
